import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PackageMinus, Plus } from "lucide-react";
import { useParams } from "react-router-dom";
import { api, apiError, fmtDate } from "../lib/api";

export default function IssueLogPage() {
    const { siteId } = useParams();
    const [site, setSite] = useState(null);
    const [issues, setIssues] = useState([]);
    const [stock, setStock] = useState([]);
    const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
    const [form, setForm] = useState({ item_name: "", qty: "", unit_raw: "", to_person_or_floor: "", note: "" });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    async function refresh() {
        setLoading(true);
        try {
            const [s, i] = await Promise.all([
                api.get(`/sites/${siteId}`),
                api.get("/issue-log", { params: { site_id: siteId, month } }),
            ]);
            setSite(s.data);
            setIssues(i.data || []);
            // Optionally fetch stock; endpoint exists via consumables — skip if it 403s.
            try {
                const st = await api.get(`/sites/${siteId}/consumables`);
                setStock(st.data || []);
            } catch { setStock([]); }
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [siteId, month]);

    async function submit(e) {
        e.preventDefault();
        if (!form.item_name.trim() || !form.qty || parseFloat(form.qty) <= 0) {
            toast.error("Item + positive qty required"); return;
        }
        setSaving(true);
        try {
            const today = new Date().toISOString().slice(0, 10);
            await api.post("/issue-log", {
                site_id: siteId, date: today,
                item_name: form.item_name.trim(),
                qty: parseFloat(form.qty),
                unit_raw: form.unit_raw || null,
                to_person_or_floor: form.to_person_or_floor || null,
                note: form.note || null,
            });
            toast.success("Issued");
            setForm({ item_name: "", qty: "", unit_raw: "", to_person_or_floor: "", note: "" });
            refresh();
        } catch (e2) { toast.error(apiError(e2)); }
        finally { setSaving(false); }
    }

    if (loading) return <div className="mx-auto max-w-5xl p-6"><div className="h-64 rounded-xl bg-white animate-pulse" /></div>;

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-8" data-testid="issue-log-page">
            <div>
                <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Consumables · Daily issue log</div>
                <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1 flex items-center gap-2">
                    <PackageMinus className="w-6 h-6" /> {site?.name}
                </h1>
                <p className="text-sm text-gray-500 mt-1">Log every issue (housekeeping, pantry, tea/coffee) — stock decrements live.</p>
            </div>

            <form onSubmit={submit} className="mt-6 rounded-xl bg-white border border-gray-100 fx-shadow-card p-4" data-testid="issue-form">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    <label className="text-xs md:col-span-2">
                        <span className="block text-gray-500 mb-1">Item</span>
                        <input
                            list="stock-items"
                            value={form.item_name}
                            onChange={(e) => setForm({ ...form, item_name: e.target.value })}
                            className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm bg-white"
                            placeholder="e.g. Coffee Beans (Beans 2 Coffee)"
                            data-testid="issue-item"
                            required
                        />
                        <datalist id="stock-items">
                            {stock.map((s, i) => <option key={i} value={s.name || s.item_name} />)}
                        </datalist>
                    </label>
                    <label className="text-xs">
                        <span className="block text-gray-500 mb-1">Qty</span>
                        <input type="number" step="0.01" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm bg-white" data-testid="issue-qty" required />
                    </label>
                    <label className="text-xs">
                        <span className="block text-gray-500 mb-1">Unit</span>
                        <input value={form.unit_raw} onChange={(e) => setForm({ ...form, unit_raw: e.target.value })} placeholder="Pack / Box / PC" className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm bg-white" data-testid="issue-unit" />
                    </label>
                    <label className="text-xs">
                        <span className="block text-gray-500 mb-1">Issued to</span>
                        <input value={form.to_person_or_floor} onChange={(e) => setForm({ ...form, to_person_or_floor: e.target.value })} placeholder="Name or Floor" className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm bg-white" data-testid="issue-to" />
                    </label>
                </div>
                <label className="text-xs block mt-3">
                    <span className="block text-gray-500 mb-1">Note (optional)</span>
                    <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm bg-white" data-testid="issue-note" />
                </label>
                <div className="mt-3 flex justify-end">
                    <button disabled={saving} className="inline-flex items-center gap-1.5 rounded-md bg-[#1E3355] hover:bg-[#15243C] text-white text-sm font-semibold px-4 py-1.5" data-testid="issue-submit">
                        <Plus className="w-4 h-4" /> {saving ? "Issuing…" : "Log issue"}
                    </button>
                </div>
            </form>

            <div className="mt-6 flex justify-between items-end gap-3">
                <div className="text-sm font-semibold text-[#1E3355]">Issue log · {month}</div>
                <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-md border border-gray-300 px-2 py-1 text-xs bg-white" data-testid="issue-month" />
            </div>
            <div className="mt-2 rounded-xl bg-white border border-gray-100 fx-shadow-card overflow-hidden">
                {issues.length === 0 ? (
                    <div className="p-6 text-center text-sm text-gray-500" data-testid="no-issues">No issues this month.</div>
                ) : (
                    <div className="max-h-[60vh] overflow-auto">
                        <table className="w-full text-xs">
                            <thead className="bg-[#F4F6F9] sticky top-0">
                                <tr>
                                    <th className="text-left p-2">Date</th>
                                    <th className="text-left p-2">Item</th>
                                    <th className="text-right p-2">Qty</th>
                                    <th className="text-left p-2">Unit</th>
                                    <th className="text-left p-2">Issued to</th>
                                    <th className="text-left p-2">Note</th>
                                </tr>
                            </thead>
                            <tbody>
                                {issues.map((r) => (
                                    <tr key={r.id} className="border-t border-gray-100">
                                        <td className="p-2">{fmtDate(r.date)}</td>
                                        <td className="p-2 font-medium">{r.item_name}</td>
                                        <td className="p-2 text-right font-mono">{r.qty}</td>
                                        <td className="p-2 text-gray-500">{r.unit_raw || "—"}</td>
                                        <td className="p-2">{r.to_person_or_floor || "—"}</td>
                                        <td className="p-2 text-gray-500">{r.note || ""}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
